import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, Users, Newspaper, Archive, Sparkles, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import FilterBar from "@/components/FilterBar";
import OpportunityCard from "@/components/OpportunityCard";
import { useAuth } from "@/context/AuthContext";
import StaffManager from "@/pages/StaffManager";
import BlogAdmin from "@/pages/BlogAdmin";
import Analytics from "@/pages/Analytics";

export default function AdminDashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [year, setYear] = useState("");
  const initialTab = location.state?.tab || "live";
  const [tab, setTab] = useState(initialTab);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_expired: true };
      if (type) params.type = type;
      if (year) params.year_group = year;
      const { data } = await api.get("/opportunities", { params });
      setOpps(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [type, year]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = { include_expired: true };
        if (type) params.type = type;
        if (year) params.year_group = year;
        const { data } = await api.get("/opportunities", { params });
        if (!cancel) setOpps(data);
      } catch (e) {
        if (!cancel) toast.error(formatApiError(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [type, year, location.key]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this opportunity? This cannot be undone.")) return;
    try {
      await api.delete(`/opportunities/${id}`);
      toast.success("Opportunity deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const active = opps.filter((o) => !o.is_expired);
  const expired = opps.filter((o) => o.is_expired);
  const showingLive = tab === "live";
  const list = showingLive ? active : expired;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-600 mb-1">Staff Dashboard</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Hello, {user?.name || "Staff"} 👋</h1>
          </div>
          <div className="flex gap-2">
            {tab === "blog" ? (
              <Link to="/staff/blog/new" className="brut-btn-primary" data-testid="new-blog-btn-top">
                <Plus className="h-4 w-4" /> New post
              </Link>
            ) : (tab === "live" || tab === "archive") ? (
              <Link to="/staff/new" className="brut-btn-primary" data-testid="new-opportunity-btn">
                <Plus className="h-4 w-4" /> New opportunity
              </Link>
            ) : null}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b-2 border-black flex-wrap">
          <TabBtn active={tab === "live"} onClick={() => setTab("live")} icon={<Sparkles className="h-4 w-4" />} testId="tab-live">
            Live <span className="ml-1 brut-tag bg-[#BBF7D0]">{active.length}</span>
          </TabBtn>
          <TabBtn active={tab === "archive"} onClick={() => setTab("archive")} icon={<Archive className="h-4 w-4" />} testId="tab-archive">
            Archive <span className="ml-1 brut-tag bg-[#FECACA]">{expired.length}</span>
          </TabBtn>
          <TabBtn active={tab === "blog"} onClick={() => setTab("blog")} icon={<Newspaper className="h-4 w-4" />} testId="tab-blog">
            Blog
          </TabBtn>
          <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} icon={<BarChart3 className="h-4 w-4" />} testId="tab-analytics">
            Analytics
          </TabBtn>
          <TabBtn active={tab === "staff"} onClick={() => setTab("staff")} icon={<Users className="h-4 w-4" />} testId="tab-staff">
            Staff
          </TabBtn>
        </div>

        {tab === "staff" ? (
          <StaffManager />
        ) : tab === "blog" ? (
          <BlogAdmin />
        ) : tab === "analytics" ? (
          <Analytics />
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              <StatCard label="Total" value={opps.length} bg="#FEF3C7" testId="stat-total" />
              <StatCard label="Live" value={active.length} bg="#BBF7D0" testId="stat-live" />
              <StatCard label="Archived" value={expired.length} bg="#FECACA" testId="stat-expired" />
            </div>

            <div className="brut-card-static p-5 mb-6">
              <FilterBar type={type} setType={setType} year={year} setYear={setYear} />
            </div>

            <div className="mb-4">
              <p className="text-sm text-zinc-600 font-medium">
                {showingLive
                  ? "Live items are visible on the public board. When their expiry date passes they move here to the Archive automatically."
                  : "Archived items are hidden from students. Restore one by opening it and pushing the expiry date forward."}
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20" data-testid="admin-loading">
                <Loader2 className="h-8 w-8 animate-spin text-[#C8102E]" />
              </div>
            ) : list.length === 0 ? (
              <div className="brut-card-static p-12 text-center" data-testid="admin-empty">
                <div className="font-display text-2xl font-black mb-2">
                  {showingLive ? "No live opportunities" : "Archive is empty"}
                </div>
                <p className="text-zinc-700 mb-4 font-medium">
                  {showingLive ? "Add your first careers opportunity to get started." : "Nothing has expired yet — good work keeping things fresh!"}
                </p>
                {showingLive && (
                  <Link to="/staff/new" className="brut-btn-primary inline-flex">
                    <Plus className="h-4 w-4" /> New opportunity
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {list.map((o) => (
                  <div key={o.id} className="relative">
                    <OpportunityCard
                      opp={o}
                      to={`/staff/edit/${o.id}`}
                      showExpired
                      footer={
                        <div className="flex gap-2 mt-4 pt-3 border-t-2 border-black">
                          <Link
                            to={`/staff/edit/${o.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="brut-btn-secondary text-xs py-1.5 px-3 flex-1"
                            data-testid={`edit-opp-${o.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Link>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(o.id); }}
                            className="brut-btn-danger text-xs py-1.5 px-3"
                            data-testid={`delete-opp-${o.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function TabBtn({ active, onClick, children, icon, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 font-display font-bold text-sm border-2 border-b-0 -mb-[2px] rounded-t-lg inline-flex items-center gap-1.5 ${active ? "border-black bg-white" : "border-transparent text-zinc-500 hover:text-black"}`}
      data-testid={testId}
    >
      {icon}
      {children}
    </button>
  );
}

function StatCard({ label, value, bg, testId }) {
  return (
    <div className="brut-card-static p-5" style={{ backgroundColor: bg }} data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-black/70">{label}</div>
      <div className="font-display font-black text-4xl mt-1">{value}</div>
    </div>
  );
}
